import React, { memo, useCallback } from 'react'
import { alpha, Stack, Typography } from '@mui/material'
import { blue, red } from '../../assets/colors'
import { Security } from '../../proto/generated/security'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../../proto/generated/order'
import { ExtraSmallButtonHoverable, NonInteractiveTooltip } from '../custom'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import Chart from '../../assets/icons/Chart'
import OptionChain from '../../assets/icons/OptionChain'
import { generatePath } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { ViewType } from '../../assets/constants'
import { handleOverviewSecurityClick } from './SecurityOverviewHeader'
import store from '../../store'
import { selectPrimarySecurity } from '../../selectors/home'
import { useDispatch } from 'react-redux'
import { setQuickOrderData } from '../../reducer/orders.reducer'
import { exchangeToJSON } from '../../proto/generated/common'
import { isTradeableSecurity } from '../../util/cashInstrument'

export type IndexComponentRowActionsProps = {
    security: Security
    onToggleExpand: () => void
}

const IndexComponentRowActions = ({ security, onToggleExpand }: IndexComponentRowActionsProps) => {
    const dispatch = useDispatch()

    const handleOptionChainClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault()
            event.stopPropagation()
            AnalyticsManager.logEvent('Navigation Button Clicked', {
                source: 'watchlist',
                buttonType: ViewType.OPTION_CHAIN,
            })
            const { expressionMode } = selectPrimarySecurity(store.getState())
            if (expressionMode) return
            window.open(
                generatePath(ROUTES.optionChain, {
                    exchange: exchangeToJSON(security.exchange),
                    symbol: security.code,
                }),
                '_blank'
            )
        },
        [security]
    )

    const handleChartClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            handleOverviewSecurityClick(security)
        },
        [security]
    )

    const handleQuickTrade = useCallback(
        (
            transaction: TransactionType,
            orderType: OrderType = OrderType.NORMAL,
            brokerOrderType: Broker_OrderType = Broker_OrderType.MARKET_ORDER,
            product: Product = Product.CNC,
            price: number | undefined = undefined
        ) => {
            AnalyticsManager.logEvent(`${transaction === TransactionType.BUY ? 'Buy' : 'Sell'} Clicked`, {
                source: 'chart-grid',
                symbol: security.symbol,
            })
            const quantity = security.lotSize || 1
            dispatch(
                setQuickOrderData({
                    openDialog: true,
                    mode: orderType === OrderType.TRIGGER ? 'trigger' : 'regular',
                    type: 'trade',
                    isSquareOffOrder: false,
                    order: {
                        transactionType: transaction,
                        security,
                        editOrder: undefined,
                        brokerOrderType,
                        orderType,
                        product,
                        price: brokerOrderType === Broker_OrderType.MARKET_ORDER ? undefined : price,
                        triggerPrice: orderType === OrderType.TRIGGER ? price : undefined,
                        quantity,
                        maxQuantity: 0,
                    },
                })
            )
        },
        [dispatch, security]
    )

    const handleExpandClick = useCallback(
        (clickEvent: React.MouseEvent<HTMLSpanElement>) => {
            clickEvent.stopPropagation()
            onToggleExpand()
        },
        [onToggleExpand]
    )

    return (
        <Stack
            spacing={0.5}
            direction="row"
            className="action-btns"
            sx={{
                position: 'absolute',
                right: 2,
                zIndex: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '28px',
                visibility: 'hidden',
                '.MuiTableRow-root:hover &': { visibility: 'visible' },
            }}
        >
            {security.hasFo && (
                <NonInteractiveTooltip
                    placement="top"
                    arrow
                    title={
                        <Typography variant="caption" sx={{ maxWidth: 'none', whiteSpace: 'nowrap' }}>
                            Option Chain
                        </Typography>
                    }
                >
                    <ExtraSmallButtonHoverable variant="outlined" color="inherit" sx={{ maxWidth: '32px', padding: 0 }} onClick={handleOptionChainClick}>
                        <OptionChain sx={{ fontSize: '28px' }} />
                    </ExtraSmallButtonHoverable>
                </NonInteractiveTooltip>
            )}
            <NonInteractiveTooltip
                placement="top"
                arrow
                title={
                    <Typography variant="caption" sx={{ maxWidth: 'none', whiteSpace: 'nowrap' }}>
                        Charts
                    </Typography>
                }
            >
                <ExtraSmallButtonHoverable variant="outlined" color="inherit" sx={{ maxWidth: '32px', padding: 0 }} onClick={handleChartClick}>
                    <Chart sx={{ fontSize: '28px' }} />
                </ExtraSmallButtonHoverable>
            </NonInteractiveTooltip>
            {isTradeableSecurity(security) && (
                <>
                    <NonInteractiveTooltip placement="top" arrow title={<Typography variant="caption">Buy</Typography>}>
                        <ExtraSmallButtonHoverable
                            variant="outlined"
                            color="inherit"
                            sx={{
                                height: '28px',
                                width: '28px',
                                padding: 0,
                                backgroundColor: alpha(blue, 0.8),
                                color: '#fff',
                                '&:hover': { backgroundColor: blue },
                                '&:active': { backgroundColor: blue },
                            }}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleQuickTrade(TransactionType.BUY)
                            }}
                        >
                            B
                        </ExtraSmallButtonHoverable>
                    </NonInteractiveTooltip>
                    <NonInteractiveTooltip placement="top" arrow title={<Typography variant="caption">Sell</Typography>}>
                        <ExtraSmallButtonHoverable
                            variant="outlined"
                            color="inherit"
                            sx={{
                                height: '28px',
                                width: '28px',
                                padding: 0,
                                background: alpha(red, 0.8),
                                color: '#fff',
                                '&:hover': { backgroundColor: red },
                                '&:active': { backgroundColor: red },
                            }}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleQuickTrade(TransactionType.SELL)
                            }}
                        >
                            S
                        </ExtraSmallButtonHoverable>
                    </NonInteractiveTooltip>
                </>
            )}
            <NonInteractiveTooltip placement="top" arrow title={<Typography variant="caption">Market Depth</Typography>}>
                <ExtraSmallButtonHoverable
                    variant="outlined"
                    color="inherit"
                    sx={{ height: '28px', width: '28px', padding: 0 }}
                    onClick={handleExpandClick}
                >
                    <FormatAlignCenterIcon sx={{ fontSize: '16px' }} />
                </ExtraSmallButtonHoverable>
            </NonInteractiveTooltip>
        </Stack>
    )
}

export default memo(IndexComponentRowActions)
